import { useState, useRef, useEffect } from 'react'
import { Send, Mic, Paperclip, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { VoiceRecordingModal } from './VoiceRecordingModal'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
  placeholder?: string
}

export function ChatInput({ onSend, disabled, placeholder }: ChatInputProps) {
  const [input, setInput] = useState('')
  const [showVoiceModal, setShowVoiceModal] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px'
    }
  }, [input])

  const handleSend = () => {
    if (input.trim() && !disabled) {
      onSend(input.trim())
      setInput('')
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleVoiceTranscript = (transcript: string) => {
    setInput(transcript)
  }

  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-end gap-2">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || "Nachricht eingeben..."}
            disabled={disabled}
            rows={1}
            className="w-full bg-transparent resize-none outline-none text-white placeholder-gray-500 pr-10 max-h-32 scrollbar-hide"
          />
          
          {/* Character count */}
          {input.length > 0 && (
            <span className="absolute right-2 bottom-0 text-xs text-gray-500">
              {input.length}
            </span>
          )}
        </div>

        {/* Voice input button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowVoiceModal(true)}
          disabled={disabled}
          className="p-2 rounded-lg transition-smooth hover:bg-white/10 text-gray-400 hover:text-white"
        >
          <Mic className="w-5 h-5" />
        </motion.button>

        {/* Send button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleSend}
          disabled={disabled || !input.trim()}
          className={`p-2 rounded-lg transition-smooth ${
            input.trim() && !disabled
              ? 'bg-primary-600 hover:bg-primary-700 text-white' 
              : 'bg-gray-700 text-gray-500 cursor-not-allowed'
          }`}
        >
          <Send className="w-5 h-5" />
        </motion.button>
      </div>

      {/* Voice Recording Modal */}
      <VoiceRecordingModal
        isOpen={showVoiceModal}
        onClose={() => setShowVoiceModal(false)}
        onTranscript={handleVoiceTranscript}
        initialText={input}
      />
    </div>
  )
}